import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Collapse, List, Tooltip} from "@mui/material";
import {useNavigate, useLocation, matchPath} from "react-router-dom";

import {useIsMobile} from "~/hooks";

import {styles} from "./SidebarSubList.styles";
import {SidebarItem} from "../SidebarItem/SidebarItem";
import {SidebarItemContent} from "../SidebarItemContent/SidebarItemContent";

type SidebarSubListProps = {
  id: string;
  title: string;
  path: string | string[];
  tooltipTitle: string;
  icon: React.ReactElement;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export const SidebarSubList: React.FC<SidebarSubListProps> = ({
  id: _id,
  title,
  path,
  icon,
  tooltipTitle,
  children,
  disabled,
  onClick,
}) => {
  const [, isMobile] = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const paths = useMemo(() => (Array.isArray(path) ? path : [path]), [path]);
  const match = useMemo(
    () => paths.some((p) => matchPath({path: p, end: false}, location.pathname)),
    [paths, location.pathname],
  );

  const [isOpen, setIsOpen] = useState(match);

  const selectFirstPathHandler = useCallback(() => {
    if (isMobile) return;
    if (isOpen) return;

    const currentMatch = matchPath({path: paths[0], end: false}, location.pathname);
    if (paths[0] && !currentMatch) {
      navigate(paths[0]);
    }
  }, [navigate, isOpen, paths, location.pathname, isMobile]);

  const menuToggle = useCallback(() => {
    if (disabled) return;
    onClick?.();
    selectFirstPathHandler();
    setIsOpen((prev) => !prev);
  }, [selectFirstPathHandler, disabled, onClick]);

  useEffect(() => {
    setIsOpen(match);
  }, [match]);

  return (
    <>
      <SidebarItem onClick={menuToggle} disabled={disabled}>
        <Tooltip title={disabled ? tooltipTitle : ""}>
          <SidebarItemContent text={title} icon={icon} hasChildren isOpen={isOpen} />
        </Tooltip>
      </SidebarItem>
      <Collapse className={styles.collapse} in={isOpen} timeout="auto" unmountOnExit>
        <List className={styles.sidebarSubList} component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </>
  );
};
